import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Building2, Camera, Edit2, KeyRound } from "lucide-react";
import AppModal from "../../components/AppModal";
import ResourcePage from "../../components/ResourcePage";
import { fetchCompanyProfile, updateCompanyProfile } from "../../redux/userSlice";
import { fileUrl } from "../../utils/files";
import { notify } from "../../utils/toastAlert";

const fields = [
  "logotipo",
  "nome",
  "nif",
  "email",
  "telefone",
  "telefone_alternativo",
  "endereco",
  "pais",
  "provincia",
  "cidade",
  "codigo_postal",
  "website",
  "regime_tributario",
  "private_key_base64",
];

const labels = {
  nome: "Empresa",
  nif: "NIF",
  email: "Email",
  telefone: "Telefone",
  telefone_alternativo: "Telefone alternativo",
  endereco: "Endereço",
  pais: "País",
  provincia: "Província",
  cidade: "Cidade",
  codigo_postal: "Código postal",
  website: "Website",
  regime_tributario: "Regime tributário",
  private_key_base64: "Chave privada da empresa",
};

const editableFields = fields.filter((field) => field !== "logotipo");

const closeModal = (id) => {
  const element = document.getElementById(id);
  if (!element || !window.bootstrap) return;
  window.bootstrap.Modal.getOrCreateInstance(element).hide();
};

const decodePrivateKey = (value) => {
  if (!value) return "";
  if (value.includes("-----BEGIN PRIVATE KEY-----")) return value;

  try {
    return atob(value);
  } catch (error) {
    return value;
  }
};

const isValidPrivateKeyPem = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return true;

  return (
    trimmed.startsWith("-----BEGIN PRIVATE KEY-----") &&
    trimmed.endsWith("-----END PRIVATE KEY-----") &&
    trimmed.split("\n").length >= 3
  );
};

const CompanyProfile = () => {
  const dispatch = useDispatch();
  const { empresa, loading, error, user } = useSelector((state) => state.user);
  const [form, setForm] = useState({});
  const [logotipo, setLogotipo] = useState(null);

  useEffect(() => {
    dispatch(fetchCompanyProfile());
  }, [dispatch]);

  useEffect(() => {
    const next = {};
    fields.forEach((field) => {
      next[field] = field === "private_key_base64"
        ? decodePrivateKey(empresa?.[field] || "")
        : empresa?.[field] || "";
    });
    setForm(next);
  }, [empresa]);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isValidPrivateKeyPem(form.private_key_base64 || "")) {
      await notify("A chave privada deve estar no formato PEM: -----BEGIN PRIVATE KEY----- ... -----END PRIVATE KEY-----", "error");
      return;
    }

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => data.append(key, value || ""));
    if (logotipo) data.append("logotipo", logotipo);

    const result = await dispatch(updateCompanyProfile(data));

    if (updateCompanyProfile.fulfilled.match(result)) {
      await notify("Perfil da empresa actualizado com sucesso.", "success");
      setLogotipo(null);
      closeModal("companyProfileModal");
    } else {
      await notify(result.payload || "Não foi possível actualizar o perfil da empresa.", "error");
    }
  };

  const canManage = user?.nivel === "admin";

  return (
    <ResourcePage
      title="Perfil da Empresa"
      breadcrumb={["Definições", "Perfil da Empresa"]}
      actions={(
        <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="modal" data-bs-target="#companyProfileModal" disabled={!canManage}>
          <Edit2 size={16} />
          Editar
        </button>
      )}
    >
      <div className="card app-card bg-white">
        <div className="card-body">
          {error && <div className="alert alert-danger py-2">{error}</div>}
          {!canManage && <div className="alert alert-warning py-2">Apenas administradores podem alterar o perfil da empresa.</div>}

          <div className="company-profile-head">
            <div className="company-logo-preview">
              {empresa?.logotipo ? <img src={fileUrl(empresa.logotipo)} alt={empresa.nome || "Empresa"} /> : <Building2 size={34} />}
            </div>
            <div>
              <h4 className="mb-1">{empresa?.nome || "Empresa"}</h4>
              <p className="text-muted mb-0">{empresa?.nif ? `NIF: ${empresa.nif}` : "Sem NIF configurado"}</p>
            </div>
          </div>

          <div className="row mt-4">
            {fields.filter((field) => field !== "private_key_base64" && field !== "logotipo").map((field) => (
              <div className="col-md-4 mb-3" key={field}>
                <span className="text-muted d-block font-size-12">{labels[field]}</span>
                <strong>{empresa?.[field] || "-"}</strong>
              </div>
            ))}
            <div className="col-12">
              <div className="border rounded p-3 bg-light">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <KeyRound size={18} className="text-primary" />
                  <strong>Chave privada da empresa</strong>
                </div>
                <code className="company-key-preview">{empresa?.private_key_base64 ? "Configurada e guardada em Base64" : "Não configurada"}</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AppModal
        id="companyProfileModal"
        title="Editar perfil da empresa"
        footer={(
          <>
            <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancelar</button>
            <button type="submit" className="btn btn-primary" form="companyProfileForm" disabled={loading || !canManage}>
              {loading ? "A guardar..." : "Guardar"}
            </button>
          </>
        )}
      >
        <form id="companyProfileForm" onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label" htmlFor="logotipo">Logotipo da empresa</label>
              <div className="input-group">
                <span className="input-group-text"><Camera size={16} /></span>
                <input className="form-control" id="logotipo" name="logotipo" type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => setLogotipo(event.target.files?.[0] || null)} />
              </div>
            </div>

            {editableFields.filter((field) => field !== "private_key_base64").map((field) => (
              <div className="col-md-6 mb-3" key={field}>
                <label className="form-label" htmlFor={field}>{labels[field]}</label>
                <input className="form-control" id={field} name={field} value={form[field] || ""} onChange={handleChange} />
              </div>
            ))}
            <div className="col-12">
              <label className="form-label" htmlFor="private_key_base64">Chave privada da empresa</label>
              <textarea className="form-control font-monospace" id="private_key_base64" name="private_key_base64" rows="8" value={form.private_key_base64 || ""} onChange={handleChange} placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----" />
              <small className="text-muted d-block mt-1">Cole a chave no formato PEM. Ao guardar, o sistema valida e armazena em Base64.</small>
            </div>
          </div>
        </form>
      </AppModal>
    </ResourcePage>
  );
};

export default CompanyProfile;
